/**
 * Foundation · Timesheet — editor degli orari previsti settimanali (profilo orario).
 * value: { A: { "0": [{start,end}], ... }, B: {...} } · weekday 0 = lunedì.
 * onProposeHours(weekKey, weekdayIdx, hours): suggerisce le ore del giorno dalla somma delle fasce.
 */
import React from "react";
import { WEEKDAYS_FULL } from "@/lib/constants";
import { formatDuration, useDurationFormat } from "@/foundation/duration";
import { computeDay, normalizeTimeInput, hasErrors } from "./timeCore";

const COUNT = 2;
const norm = (ivs) => Array.from({ length: COUNT }, (_, i) => ({ start: ivs?.[i]?.start || "", end: ivs?.[i]?.end || "" }));

export function WeeklyScheduleEditor({ value, onChange, week = "A", dayHours = {}, onProposeHours, testid = "schedule" }) {
  const fmt = useDurationFormat();
  const days = value?.[week] || {};
  const setCell = (wd, i, key, v) => {
    const ivs = norm(days[wd]);
    ivs[i] = { ...ivs[i], [key]: v };
    onChange({ ...(value || {}), [week]: { ...days, [wd]: ivs } });
  };
  return (
    <div className="space-y-1" data-testid={`${testid}-${week}`}>
      {WEEKDAYS_FULL.map((label, wd) => {
        const ivs = norm(days[String(wd)]);
        const res = computeDay(ivs);
        const planned = res.workedMinutes / 60;
        const declared = Number(dayHours[wd] || 0);
        const mismatch = res.workedMinutes > 0 && Math.round(declared * 60) !== res.workedMinutes;
        return (
          <div key={wd} className="grid grid-cols-[90px_repeat(4,64px)_1fr] items-center gap-1.5 text-xs">
            <span className="text-suite-neutral-600">{label}</span>
            {ivs.flatMap((iv, i) => ["start", "end"].map((key) => (
              <input key={`${i}-${key}`} value={iv[key]} placeholder="--:--" inputMode="numeric" autoComplete="off"
                onChange={(e) => setCell(String(wd), i, key, e.target.value.replace(/[^\d:.,]/g, "").slice(0, 5))}
                onBlur={(e) => setCell(String(wd), i, key, normalizeTimeInput(e.target.value))}
                className={`h-7 rounded border px-1 text-center font-mono ${hasErrors(res) ? "border-suite-error bg-red-50" : "border-suite-neutral-200"}`}
                aria-label={`${label} ${key === "start" ? "entrata" : "uscita"} ${i + 1}`}
                data-testid={`${testid}-${week}-${wd}-${key === "start" ? "in" : "out"}-${i + 1}`} />
            )))}
            <span className="flex items-center gap-2">
              {res.workedMinutes > 0 && <span className="font-mono text-suite-neutral-500">{formatDuration(planned, fmt)}</span>}
              {hasErrors(res) && <span className="text-suite-error">{res.issues.find((x) => x.level === "error").message}</span>}
              {!hasErrors(res) && mismatch && (
                <button type="button" className="text-amber-700 hover:underline" onClick={() => onProposeHours?.(week, wd, planned)}
                  data-testid={`${testid}-${week}-${wd}-propose`}>
                  Ore giorno {formatDuration(declared, fmt)} ≠ orari: usa {formatDuration(planned, fmt)}
                </button>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default WeeklyScheduleEditor;
